import React, { SyntheticEvent, useCallback, useMemo, useState } from 'react'
import '@css/common/QuillEditor.scss'
import { Button, Modal, OverlayTrigger, Popover } from 'react-bootstrap'
import {
  GetHostnamesDocument,
  GetMeetupDocument,
  GetMentionedUserDocument,
  HtmlWithMentions,
  Maybe,
  Meetup,
  MeetupInput,
  RegisterMeetupAttendeeDocument,
} from '~/api/generated/graphql'
import { asString, getVideoStartTime, getWistiaId, isSafeLink, updateWistiaLink } from '~/utils'
import { EmployeeBadgeComponent } from '~/common/EmployeeBadgeComponent'
import { ApolloClient, ApolloProvider, useApolloClient, useMutation, useQuery } from '@apollo/client'
import '@css/common/MentionEmployeeBadgeComponent.scss'
import { useNavigate } from 'react-router'
import { VideoContent } from '~/pages/posts/MediaComponent'
import { AuthProvider, useAuth } from '~/auth/Auth'
import { WindowProvider } from '~/common/hooks/useWindowSize'
import { elementClicked } from '~/common/EventLogger'
import ToastComponent from '~/common/ToastComponent'
import parse, { attributesToProps, domToReact, HTMLReactParserOptions } from 'html-react-parser'
import { DOMNode, Element } from 'html-dom-parser'
import { ElementType } from 'domelementtype'

type Props = {
  expandableRef?: React.RefObject<HTMLDivElement | null>
  className?: string
  value?: Maybe<HtmlWithMentions>
  postHeader?: boolean
  meetup?: Meetup
  commentId?: string
  postId?: string
  primaryVideoUrl?: Maybe<string>
  repostId?: Maybe<string>
}

// The Overlay base types have explicit any in the base definition, so we have to carry them through here and
// disable the lint check
interface RenderTooltipProps extends React.ComponentPropsWithRef<'div'> {
  userId: string
  clickEmployee: () => void
}

const RenderedTooltip = ({ ref, userId, clickEmployee, ...props }: RenderTooltipProps) => {
  return (
    <Popover ref={ref} {...props}>
      <Popover.Body className={'mention-tooltip-body'}>
        <EmployeeBadgeComponent userId={userId} isMention={true} clickEmployee={clickEmployee} />
      </Popover.Body>
    </Popover>
  )
}

const MentionButton = ({
  dataId,
  dataValue,
  atChar,
  clickMention,
  showHover,
  link,
}: {
  dataValue: string
  dataId: string
  atChar: string
  clickMention: () => void
  showHover: boolean
  link: string
}) => {
  const { data, loading } = useQuery(GetMentionedUserDocument, {
    variables: {
      id: dataId,
    },
    skip: dataValue === 'All',
  })
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    clickMention()
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const overlay = (props: any) => {
    return <RenderedTooltip userId={dataId} clickEmployee={clickMention} {...props} />
  }

  const button = data?.user?.hidden ? (
    <div data-testid={'at-mention-link'}>{`@${dataValue}`}</div>
  ) : (
    <a href={link} data-testid={'at-mention-link'}>
      <Button onClick={handleClick} variant={'link'} className={'mention-link'}>
        {atChar}
        {dataValue}
      </Button>
    </a>
  )
  if (loading) return <div data-testid={'at-mention-link'}>{`@${dataValue}`}</div>
  // noinspection RequiredAttributes
  return showHover && !data?.user?.hidden ? (
    <OverlayTrigger placement="right" delay={{ show: 250, hide: 800 }} overlay={overlay}>
      {button}
    </OverlayTrigger>
  ) : (
    button
  )
}

type LinkWithSafetyProps = {
  href: string
  text: string
  domain: string
  displayLink: string
  isLongLink: boolean
  veevaDomains: string[]
  isSameHost: boolean
}
const showSafetyWarning = (e: React.MouseEvent<HTMLSpanElement>) => {
  e.preventDefault()
}

const LinkWithSafety = ({
  href,
  text,
  displayLink,
  isLongLink,
  veevaDomains,
  domain,
  isSameHost,
}: LinkWithSafetyProps) => {
  const isUnsafeLink = href != text && !isSafeLink(domain, veevaDomains)

  interface LinkSafetyTooltipProps extends React.ComponentPropsWithRef<'div'> {
    href: string
    isSameHost: boolean
  }

  const LinkSafetyTooltip = ({ ref, href, isSameHost, ...props }: LinkSafetyTooltipProps) => {
    return (
      <Popover ref={ref} {...props} className={'link-safety-popover'}>
        <Popover.Body className={'link-safety-tooltip-body'}>
          <span>
            <div className={'url'}>Visit URL:&nbsp;</div>
            <a href={href} target={isSameHost ? '' : '_blank'}>
              {displayLink}
              {isLongLink ? '/...' : ''}
            </a>
          </span>
          <div>External site</div>
        </Popover.Body>
      </Popover>
    )
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const safetyOverlay = (props: any) => {
    return <LinkSafetyTooltip href={href} isSameHost={isSameHost} {...props} />
  }

  // noinspection RequiredAttributes
  return isUnsafeLink ? (
    <OverlayTrigger
      placement="bottom-start"
      overlay={safetyOverlay}
      trigger={['click', 'focus']}
      rootClose
      offset={[-1, 2]}
    >
      <span onClick={showSafetyWarning} data-testid={'mentionable-text-link'}>
        {text}
      </span>
    </OverlayTrigger>
  ) : (
    <span data-testid={'mentionable-text-link'}>{text}</span>
  )
}

type LinkWithVideoPopoverProps = {
  client: ApolloClient<object>
  text: string
  wistiaId: string
  startTime: number
  postId?: string
}

const LinkWithVideoPopover = ({ client, text, wistiaId, startTime, postId }: LinkWithVideoPopoverProps) => {
  return (
    <ApolloProvider client={client}>
      <AuthProvider>
        <WindowProvider>
          <VideoContent
            url={`https://fast.wistia.com/embed/medias/${wistiaId}.jsonp`}
            post_id={postId ?? ''}
            playButton={<span onClick={e => e.preventDefault()}>{text}</span>}
            startTime={startTime}
          />
        </WindowProvider>
      </AuthProvider>
    </ApolloProvider>
  )
}

const MeetupButton = ({ meetupId, postId, commentId }: { meetupId?: string; postId?: string; commentId?: string }) => {
  const { authUserId } = useAuth()
  const [registerMeetupAttendee, { loading: isRegistering }] = useMutation(RegisterMeetupAttendeeDocument)
  const { data } = useQuery(GetMeetupDocument, { variables: { id: meetupId ?? '' } })
  const meetup = data?.meetup
  const [showMeetupError, setShowMeetupError] = useState(false)
  const [showOrganizerModal, setShowOrganizerModal] = useState<boolean>(false)
  const [showInviteSentModal, setShowInviteSentModal] = useState<boolean>(false)

  const handleMeetupClicked: React.MouseEventHandler<HTMLButtonElement> = useCallback(
    (e?: SyntheticEvent) => {
      if (meetup?.organizerId === authUserId) {
        setShowOrganizerModal(true)
      } else if (meetup?.currentUserRegistered) {
        setShowInviteSentModal(true)
      } else {
        if (e) {
          elementClicked(e, 'click-meetup-register', { postId: postId, commentId: commentId })
        }
        void registerMeetupAttendee({
          variables: {
            meetup: {
              gid: meetup?.gid,
              startTime: meetup?.startTime,
              link: meetup?.link,
            } as MeetupInput,
            postId: postId ?? null,
            commentId: commentId ?? null,
          },
        }).then(r => {
          if (r.data?.registerMeetupAttendee?.error && r?.data.registerMeetupAttendee?.error?.code === 'notInFuture') {
            setShowMeetupError(true)
          }
        })
      }
    },
    [authUserId, commentId, meetup, postId, registerMeetupAttendee]
  )
  return (
    <>
      <button
        data-testid={`mention-btn ${meetup?.organizerId}`}
        className={`btn-plain ${meetup?.currentUserRegistered && 'registered'}`}
        onClick={handleMeetupClicked}
        disabled={isRegistering}
      >
        <div className={`btn-title`} />
        <span>
          {isRegistering ? 'Registering...' : meetup?.currentUserRegistered ? 'Invite sent!' : 'Click to register!'}
        </span>
      </button>
      {meetup?.attendeeCount ? (
        <span className="attendee-counts">
          {meetup?.attendeeCount}
          {` ${meetup?.attendeeCount === 1 ? 'customer' : 'customers'} attending`}
        </span>
      ) : (
        ''
      )}
      <ToastComponent
        bg={'danger'}
        show={showMeetupError}
        onClose={() => {
          setShowMeetupError(false)
        }}
      >
        The meetup has already occurred
      </ToastComponent>
      <Modal className={'organizer-modal'} show={showOrganizerModal} onHide={() => setShowOrganizerModal(false)}>
        <Modal.Header>Since you're the organizer, you can't send yourself an invite!</Modal.Header>
        <Modal.Body>
          <p>When a customer clicks the button, they will get a Google invite sent on your behalf.</p>
          <a href={meetup?.link} target={'_blank'}>
            View meeting in your Google calendar
          </a>
        </Modal.Body>
        <Modal.Footer>
          <Button variant={'primary'} onClick={() => setShowOrganizerModal(false)}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={showInviteSentModal} onHide={() => setShowInviteSentModal(false)}>
        <Modal.Body>
          <p>An invitation was sent to your email from the organizer.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant={'primary'} onClick={() => setShowInviteSentModal(false)}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

const MentionableText = ({
  expandableRef,
  className = '',
  value,
  postHeader,
  meetup,
  commentId,
  postId,
  primaryVideoUrl,
  repostId,
}: Props) => {
  const client = useApolloClient()
  const navigate = useNavigate()

  const { data: hostnamesData, loading: loadingHostnames } = useQuery(GetHostnamesDocument)
  const veevaDomains = useMemo(() => {
    if (loadingHostnames) return
    return (hostnamesData?.hostnames?.edges.map(e => e?.node?.name) as string[]) ?? []
  }, [hostnamesData, loadingHostnames])

  const savedExpandTitle = expandableRef?.current?.title ?? ''
  // Workaround for tooltip behavior on Safari
  const toggleParentHoverText = (hide: boolean) => {
    if (expandableRef?.current && hide) {
      expandableRef.current.title = ''
    }
    if (expandableRef?.current && !hide) {
      expandableRef.current.title = savedExpandTitle
    }
  }

  const wrapMention = (elem: Element) => {
    const dataLink = elem.attribs['data-link'] || ''
    const dataId = elem.attribs['data-id'] || ''
    const dataValue = elem.attribs['data-value'] || ''
    const atChar = elem.attribs['data-denotation-char'] || ''
    const nav = () => {
      void navigate(dataLink)
    }
    const attribs = { ...elem.attribs }
    return (
      <span className="mention" {...attributesToProps(attribs)}>
        <MentionButton
          link={dataLink}
          clickMention={nav}
          dataId={dataId}
          dataValue={dataValue}
          atChar={atChar}
          showHover={dataLink.includes('/profiles')}
        />
      </span>
    )
  }

  const wrapMeetup = (m: Element) => {
    const attribs = Object.fromEntries(Object.entries(m.attribs).filter(([key, _]) => key.startsWith('data-')))

    return (
      <div className={'meetup'} {...attributesToProps(attribs)}>
        <MeetupButton meetupId={meetup?.meetupId ?? ''} postId={postId} commentId={commentId} />
      </div>
    )
  }

  const wrapLink = (link: Element) => {
    const href = link.attribs['href'] ?? ''
    let url
    try {
      url = new URL(href)
    } catch {
      return
    }
    const displayLink = `${url.protocol ? url.protocol + '//' : ''}${url.hostname}`
    // determines whether we have an ellipsis after the display link
    const isLongLink = Boolean(url.port || url.pathname != '/' || url.search || url.hash)
    // if the link is within the current hostname we want to open it in the same tab
    const isSameHost = globalThis.location.host == `${url.hostname}${url.port ? ':' + url.port : ''}`
    const attribs = link.attribs
    attribs.target = isSameHost ? '' : '_blank'
    const linkPath = url.pathname + url.search
    const videoScanMatches = linkPath.match(
      /\/communities\/[\w-]+\/posts\/(?<postId>[\w-]+)\?(\S+)?t=(?<timestamp>\d\d:\d\d:\d\d)/
    )
    const updatedUrl = updateWistiaLink(primaryVideoUrl ?? null)
    const wistiaId = getWistiaId(updatedUrl)
    const text = link.children.map(child => (child.type == ElementType.Text && child.data) || '').join('') || href

    if (
      videoScanMatches?.groups?.timestamp &&
      wistiaId &&
      (videoScanMatches.groups.postId === postId || videoScanMatches.groups.postId === repostId)
    ) {
      return (
        <a {...attributesToProps(attribs)}>
          <LinkWithVideoPopover
            client={client}
            text={text}
            wistiaId={wistiaId}
            startTime={getVideoStartTime(videoScanMatches.groups.timestamp) ?? 0}
            postId={postId}
          />
        </a>
      )
    } else {
      return (
        <a {...attributesToProps(attribs)}>
          <LinkWithSafety
            href={href}
            text={text}
            displayLink={displayLink}
            isLongLink={isLongLink}
            veevaDomains={veevaDomains ?? []}
            domain={url.hostname}
            isSameHost={isSameHost}
          />
        </a>
      )
    }
  }

  const options: HTMLReactParserOptions = {
    replace: (domNode: DOMNode) => {
      if (!veevaDomains || domNode.type !== ElementType.Tag) return
      const classNames = domNode.attribs?.class // Get the raw class string
      const hasMentionClass = classNames && classNames.split(' ').includes('mention')
      if (hasMentionClass) {
        return wrapMention(domNode)
      } else if (domNode.name === 'a') {
        return wrapLink(domNode)
      } else if (domNode.name === 'meetup') {
        return wrapMeetup(domNode)
      } else if (domNode.name === 'p') {
        // p only allows inline elements, so replace it with a div
        return <div {...attributesToProps(domNode.attribs)}>{domToReact(domNode.childNodes as DOMNode[], options)}</div>
      }
    },
  }
  return (
    <div
      className={`${className} mentionable-text ${postHeader ? 'feed-post-header' : ''}`}
      title={''} // Don't inherit parent's title value
      onMouseEnter={() => toggleParentHoverText(true)}
      onMouseLeave={() => toggleParentHoverText(false)}
    >
      {parse(asString(value), options)}
    </div>
  )
}
export default MentionableText
